import { useState, useEffect, useRef } from "react";
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  ClipboardDocumentIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import PageHeader from "../../components/layout/PageHeader";
import { Card, CardHeader } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Select } from "../../components/ui/Select";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { activitiesApi } from "../../api/activities";
import {
  importsApi,
  type ImportResult,
  type AcImportResult,
  type AcBulkImportResult,
  type CounselorImportResult,
  type AcImportMode,
  type AcBulkImportMode,
} from "../../api/imports";
import { integrationsApi, type SyncResult, type IntegrationStatus, type OrgCredentials, type PurgeResult } from "../../api/integrations";
import toast from "react-hot-toast";
import type { Activity } from "../../types";
import { useAuth } from "../../hooks/useAuth";

// ─── Shared sub-components ────────────────────────────────────────────────────

function SectionToggle({
  title,
  subtitle,
  icon,
  open,
  onToggle,
}: {
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between p-5 text-left hover:bg-stone-50 transition-colors rounded-xl"
    >
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-stone-100 flex items-center justify-center text-stone-600 flex-shrink-0">
          {icon}
        </div>
        <div>
          <p className="font-semibold text-stone-900 text-sm">{title}</p>
          {subtitle && <p className="text-xs text-stone-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {open ? (
        <ChevronDownIcon className="w-4 h-4 text-stone-400 flex-shrink-0" />
      ) : (
        <ChevronRightIcon className="w-4 h-4 text-stone-400 flex-shrink-0" />
      )}
    </button>
  );
}

function ResultPanel({ result }: { result: ImportResult & Partial<AcImportResult> & Partial<AcBulkImportResult> }) {
  const hasActivity = (result.archived ?? 0) > 0 || (result.deleted ?? 0) > 0;
  return (
    <div className="mt-3 rounded-lg border border-stone-200 bg-stone-50 p-4 space-y-2 text-sm">
      <p className="font-medium text-stone-700">Import result</p>
      <div className="flex flex-wrap gap-3">
        <span className="flex items-center gap-1 text-forest-700">
          <CheckCircleIcon className="w-4 h-4" />
          {result.created} created
        </span>
        {(result.updated ?? 0) > 0 && (
          <span className="flex items-center gap-1 text-sky-700">
            <CheckCircleIcon className="w-4 h-4" />
            {result.updated} updated
          </span>
        )}
        <span className="flex items-center gap-1 text-amber-600">
          <InformationCircleIcon className="w-4 h-4" />
          {result.skipped} skipped (duplicate)
        </span>
        {hasActivity && (
          <>
            {(result.archived ?? 0) > 0 && (
              <span className="flex items-center gap-1 text-stone-500">
                <InformationCircleIcon className="w-4 h-4" />
                {result.archived} archived
              </span>
            )}
            {(result.deleted ?? 0) > 0 && (
              <span className="flex items-center gap-1 text-red-600">
                <ExclamationCircleIcon className="w-4 h-4" />
                {result.deleted} deleted
              </span>
            )}
          </>
        )}
      </div>
      {result.errors.length > 0 && (
        <div className="mt-2">
          <p className="text-xs font-medium text-red-600 mb-1">
            {result.errors.length} row error{result.errors.length !== 1 ? "s" : ""}:
          </p>
          <ul className="text-xs text-red-600 space-y-0.5 max-h-40 overflow-y-auto">
            {result.errors.map((e, i) => (
              <li key={i} className="flex items-start gap-1">
                <ExclamationCircleIcon className="w-3 h-3 mt-0.5 flex-shrink-0" />
                {e}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function TempCredentialsPanel({
  credentials,
}: {
  credentials: { email: string; tempPassword: string }[];
}) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed || credentials.length === 0) return null;

  const copyAll = () => {
    const text = credentials.map((c) => `${c.email}\t${c.tempPassword}`).join("\n");
    navigator.clipboard.writeText(text).then(() => toast.success("Copied to clipboard"));
  };

  return (
    <div className="mt-3 rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="font-medium text-sky-800">Temporary passwords generated</p>
          <p className="text-xs text-sky-600 mt-0.5">
            Share these credentials securely. These will not be shown again.
          </p>
        </div>
        <button
          onClick={copyAll}
          className="flex items-center gap-1 text-xs text-sky-700 hover:text-sky-900 font-medium whitespace-nowrap"
          title="Copy all as tab-separated"
        >
          <ClipboardDocumentIcon className="w-4 h-4" />
          Copy all
        </button>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-sky-600">
            <th className="text-left pb-1">Email</th>
            <th className="text-left pb-1 pl-4">Temporary Password</th>
          </tr>
        </thead>
        <tbody className="font-mono">
          {credentials.map((c) => (
            <tr key={c.email} className="border-t border-sky-100">
              <td className="py-1 text-sky-900">{c.email}</td>
              <td className="py-1 pl-4 text-sky-900">{c.tempPassword}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <button
        onClick={() => setDismissed(true)}
        className="mt-3 text-xs text-sky-500 hover:text-sky-700 underline"
      >
        Dismiss
      </button>
    </div>
  );
}

interface FileInputRowProps {
  accept?: string;
  onChange: (file: File | null) => void;
  file: File | null;
}

function FileInputRow({ onChange, file }: FileInputRowProps) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <div className="flex items-center gap-3 mt-3">
      <button
        type="button"
        onClick={() => ref.current?.click()}
        className="flex items-center gap-2 text-sm border border-dashed border-stone-300 rounded-lg px-3 py-2 text-stone-500 hover:text-stone-700 hover:border-stone-400 transition-colors"
      >
        <ArrowUpTrayIcon className="w-4 h-4" />
        {file ? file.name : "Choose CSV file…"}
      </button>
      {file && (
        <button
          type="button"
          onClick={() => { onChange(null); if (ref.current) ref.current.value = ""; }}
          className="text-xs text-stone-400 hover:text-stone-600"
        >
          Clear
        </button>
      )}
      <input
        ref={ref}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={(e) => onChange(e.target.files?.[0] ?? null)}
      />
    </div>
  );
}

function ColumnHint({ columns }: { columns: string }) {
  return (
    <p className="text-xs text-stone-500 mt-1">
      Expected columns: <code className="font-mono bg-stone-100 px-1 py-0.5 rounded text-xs">{columns}</code>
    </p>
  );
}

// ─── Section: ACs ─────────────────────────────────────────────────────────────

function AcsSection({ activities }: { activities: Activity[] }) {
  const [open, setOpen] = useState(false);
  const [activityId, setActivityId] = useState<number | "">("");
  const [mode, setMode] = useState<AcImportMode>("replace_archive");
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AcImportResult | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const selectedActivity = activities.find((a) => a.id === activityId);

  const handleImport = async () => {
    if (!file || !activityId) return;
    if (mode === "delete_replace" && !deleteConfirmed) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await importsApi.importAcs(activityId, mode, mode === "delete_replace", file);
      setResult(res);
      toast.success(`Import complete: ${res.created} created`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Import failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card padding={false}>
      <SectionToggle
        title="Achievement Credits (ACs)"
        icon={<span className="text-base">🎯</span>}
        open={open}
        onToggle={() => setOpen((v) => !v)}
      />
      {open && (
        <div className="px-5 pb-5 space-y-5">
          {/* Activity selector */}
          <Select
            label="Activity"
            placeholder="Select an activity…"
            value={activityId}
            onChange={(e) => { setActivityId(Number(e.target.value) || ""); setResult(null); }}
            options={activities.map((a) => ({ value: a.id, label: a.name }))}
          />

          {/* Export / Template */}
          <div className="border-t border-stone-100 pt-4">
            <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Export</p>
            <p className="text-xs text-stone-500 mb-3">
              Download ACs for the selected activity as CSV. The exported file can be edited and re-imported.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                icon={<ArrowDownTrayIcon className="w-4 h-4" />}
                disabled={!activityId || exporting}
                loading={exporting}
                onClick={async () => {
                  if (!activityId || !selectedActivity) return;
                  setExporting(true);
                  try {
                    await importsApi.downloadExport("acs", activityId, `acs-${selectedActivity.name}`);
                  } catch {
                    toast.error("Export failed");
                  } finally {
                    setExporting(false);
                  }
                }}
              >
                Export current ACs
              </Button>
              <Button
                variant="ghost"
                size="sm"
                icon={<ArrowDownTrayIcon className="w-4 h-4" />}
                disabled={!activityId || downloading}
                loading={downloading}
                onClick={async () => {
                  setDownloading(true);
                  try {
                    await importsApi.downloadTemplate("acs", activityId || undefined);
                  } catch {
                    toast.error("Download failed");
                  } finally {
                    setDownloading(false);
                  }
                }}
              >
                Blank template
              </Button>
            </div>
          </div>

          {/* Import */}
          <div className="border-t border-stone-100 pt-4">
            <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Import</p>
            <ColumnHint columns="code, name, description" />

            {/* Mode selector */}
            <div className="mt-3 space-y-2">
              {(
                [
                  { value: "replace_archive", label: "Replace — archive existing ACs and add from CSV", desc: "Default. Existing ACs are archived (not deleted) and the CSV becomes the new list." },
                  { value: "add", label: "Add — append to existing (skip duplicates by name)", desc: "Only adds new ACs. Rows whose name matches an existing AC are skipped." },
                  { value: "delete_replace", label: "Delete & replace — permanently delete existing ACs and import from CSV", desc: "Danger: existing ACs are hard-deleted along with all completion records.", danger: true },
                ] as { value: AcImportMode; label: string; desc: string; danger?: boolean }[]
              ).map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-start gap-3 cursor-pointer rounded-lg border p-3 transition-colors ${
                    mode === opt.value
                      ? opt.danger
                        ? "border-red-400 bg-red-50"
                        : "border-forest-400 bg-forest-50"
                      : "border-stone-200 hover:border-stone-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="ac-mode"
                    value={opt.value}
                    checked={mode === opt.value}
                    onChange={() => { setMode(opt.value); setDeleteConfirmed(false); }}
                    className="mt-0.5 accent-forest-600"
                  />
                  <div>
                    <p className={`text-sm font-medium ${opt.danger ? "text-red-700" : "text-stone-800"}`}>
                      {opt.label}
                    </p>
                    <p className="text-xs text-stone-500 mt-0.5">{opt.desc}</p>
                  </div>
                </label>
              ))}
            </div>

            {/* Delete confirmation */}
            {mode === "delete_replace" && (
              <label className="mt-3 flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deleteConfirmed}
                  onChange={(e) => setDeleteConfirmed(e.target.checked)}
                  className="rounded border-red-400 text-red-600 focus:ring-red-500"
                />
                <span className="text-xs text-red-700 font-medium flex items-center gap-1">
                  <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                  I understand this will permanently delete all existing ACs and their completion records
                </span>
              </label>
            )}

            <FileInputRow file={file} onChange={(f) => { setFile(f); setResult(null); }} />

            <div className="mt-3">
              <Button
                variant="primary"
                size="sm"
                loading={loading}
                disabled={!file || !activityId || (mode === "delete_replace" && !deleteConfirmed)}
                onClick={handleImport}
              >
                Import ACs
              </Button>
            </div>

            {result && <ResultPanel result={result} />}
          </div>
        </div>
      )}
    </Card>
  );
}

// ─── Section: Bulk ACs (all activities) ───────────────────────────────────────

function AcsBulkSection() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<AcBulkImportMode>("upsert");
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AcBulkImportResult | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await importsApi.importAcsBulk(mode, file);
      setResult(res);
      toast.success(`Import complete: ${res.created} created, ${res.updated} updated`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Import failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card padding={false}>
      <SectionToggle
        title="Achievement Credits — Bulk (All Activities)"
        icon={<span className="text-base">📋</span>}
        open={open}
        onToggle={() => setOpen((v) => !v)}
      />
      {open && (
        <div className="px-5 pb-5 space-y-5">
          {/* Export */}
          <div className="border-t border-stone-100 pt-4">
            <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Export</p>
            <p className="text-xs text-stone-500 mb-3">
              Download all ACs across every activity as a single CSV. Edit and re-import to update in bulk.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                icon={<ArrowDownTrayIcon className="w-4 h-4" />}
                disabled={exporting}
                loading={exporting}
                onClick={async () => {
                  setExporting(true);
                  try {
                    await importsApi.downloadExport("acs-bulk", undefined, "acs-all-activities");
                  } catch {
                    toast.error("Export failed");
                  } finally {
                    setExporting(false);
                  }
                }}
              >
                Export all ACs
              </Button>
              <Button
                variant="ghost"
                size="sm"
                icon={<ArrowDownTrayIcon className="w-4 h-4" />}
                disabled={downloading}
                loading={downloading}
                onClick={async () => {
                  setDownloading(true);
                  try {
                    await importsApi.downloadTemplate("acs-bulk");
                  } catch {
                    toast.error("Download failed");
                  } finally {
                    setDownloading(false);
                  }
                }}
              >
                Blank template
              </Button>
            </div>
          </div>

          {/* Import */}
          <div className="border-t border-stone-100 pt-4">
            <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Import</p>
            <ColumnHint columns="activity_name, code, name, description" />
            <p className="text-xs text-stone-400 mt-1">
              <code className="font-mono">activity_name</code> must match an existing activity exactly.
              ACs are matched by <code className="font-mono">code</code> — codes are unique per organisation.
            </p>

            {/* Mode selector */}
            <div className="mt-3 space-y-2">
              {(
                [
                  { value: "upsert", label: "Update or create — update existing ACs, create new ones", desc: "Default. If a code already exists its name and description are updated; otherwise a new AC is created." },
                  { value: "create_only", label: "Create only — skip rows whose code already exists", desc: "Rows matching an existing code are skipped. Only genuinely new codes are created." },
                ] as { value: AcBulkImportMode; label: string; desc: string }[]
              ).map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-start gap-3 cursor-pointer rounded-lg border p-3 transition-colors ${
                    mode === opt.value
                      ? "border-forest-400 bg-forest-50"
                      : "border-stone-200 hover:border-stone-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="ac-bulk-mode"
                    value={opt.value}
                    checked={mode === opt.value}
                    onChange={() => setMode(opt.value)}
                    className="mt-0.5 accent-forest-600"
                  />
                  <div>
                    <p className="text-sm font-medium text-stone-800">{opt.label}</p>
                    <p className="text-xs text-stone-500 mt-0.5">{opt.desc}</p>
                  </div>
                </label>
              ))}
            </div>

            <FileInputRow file={file} onChange={(f) => { setFile(f); setResult(null); }} />

            <div className="mt-3">
              <Button
                variant="primary"
                size="sm"
                loading={loading}
                disabled={!file}
                onClick={handleImport}
              >
                Import ACs
              </Button>
            </div>

            {result && <ResultPanel result={result} />}
          </div>
        </div>
      )}
    </Card>
  );
}

// ─── Generic section factory (Activities, Campers, Cabins) ────────────────────

interface GenericSectionProps {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  columns: string;
  type: string;
  importFn: (file: File) => Promise<ImportResult>;
  extraNotes?: React.ReactNode;
}

function GenericSection({
  title,
  subtitle,
  icon,
  columns,
  type,
  importFn,
  extraNotes,
}: GenericSectionProps) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await importFn(file);
      setResult(res);
      toast.success(`Import complete: ${res.created} created`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Import failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card padding={false}>
      <SectionToggle
        title={title}
        subtitle={subtitle}
        icon={icon}
        open={open}
        onToggle={() => setOpen((v) => !v)}
      />
      {open && (
        <div className="px-5 pb-5 space-y-5">
          {/* Export */}
          <div className="border-t border-stone-100 pt-4">
            <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Export</p>
            <p className="text-xs text-stone-500 mb-3">
              Download current data as CSV in import-compatible format.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                icon={<ArrowDownTrayIcon className="w-4 h-4" />}
                disabled={exporting}
                loading={exporting}
                onClick={async () => {
                  setExporting(true);
                  try {
                    await importsApi.downloadExport(type);
                  } catch {
                    toast.error("Export failed");
                  } finally {
                    setExporting(false);
                  }
                }}
              >
                Export current data
              </Button>
              <Button
                variant="ghost"
                size="sm"
                icon={<ArrowDownTrayIcon className="w-4 h-4" />}
                disabled={downloading}
                loading={downloading}
                onClick={async () => {
                  setDownloading(true);
                  try {
                    await importsApi.downloadTemplate(type);
                  } catch {
                    toast.error("Download failed");
                  } finally {
                    setDownloading(false);
                  }
                }}
              >
                Blank template
              </Button>
            </div>
          </div>

          {/* Import */}
          <div className="border-t border-stone-100 pt-4">
            <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Import</p>
            <p className="text-xs text-stone-500">
              Add new records only — existing records with matching identifiers are skipped.
            </p>
            <ColumnHint columns={columns} />
            {extraNotes}
            <FileInputRow file={file} onChange={(f) => { setFile(f); setResult(null); }} />
            <div className="mt-3">
              <Button
                variant="primary"
                size="sm"
                loading={loading}
                disabled={!file}
                onClick={handleImport}
              >
                Import {title}
              </Button>
            </div>
            {result && <ResultPanel result={result} />}
          </div>
        </div>
      )}
    </Card>
  );
}

// ─── Section: Counselors (needs temp password panel) ─────────────────────────

function CounselorsSection() {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CounselorImportResult | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await importsApi.importCounselors(file);
      setResult(res);
      toast.success(`Import complete: ${res.created} created`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Import failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card padding={false}>
      <SectionToggle
        title="Counselors / Staff"
        icon={<span className="text-base">👤</span>}
        open={open}
        onToggle={() => setOpen((v) => !v)}
      />
      {open && (
        <div className="px-5 pb-5 space-y-5">
          {/* Export */}
          <div className="border-t border-stone-100 pt-4">
            <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Export</p>
            <p className="text-xs text-stone-500 mb-3">
              Download current staff list. Password column is intentionally blank — passwords cannot be exported.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                icon={<ArrowDownTrayIcon className="w-4 h-4" />}
                disabled={exporting}
                loading={exporting}
                onClick={async () => {
                  setExporting(true);
                  try {
                    await importsApi.downloadExport("counselors");
                  } catch {
                    toast.error("Export failed");
                  } finally {
                    setExporting(false);
                  }
                }}
              >
                Export current staff
              </Button>
              <Button
                variant="ghost"
                size="sm"
                icon={<ArrowDownTrayIcon className="w-4 h-4" />}
                disabled={downloading}
                loading={downloading}
                onClick={async () => {
                  setDownloading(true);
                  try {
                    await importsApi.downloadTemplate("counselors");
                  } catch {
                    toast.error("Download failed");
                  } finally {
                    setDownloading(false);
                  }
                }}
              >
                Blank template
              </Button>
            </div>
          </div>

          {/* Import */}
          <div className="border-t border-stone-100 pt-4">
            <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Import</p>
            <p className="text-xs text-stone-500">
              Duplicate check: email address. Valid roles: admin, director, counselor, readonly (defaults to counselor).
            </p>
            <ColumnHint columns="email, first_name, last_name, role, password" />
            <p className="text-xs text-stone-400 mt-1">
              Leave the password column blank to auto-generate a temporary password.
            </p>
            <FileInputRow file={file} onChange={(f) => { setFile(f); setResult(null); }} />
            <div className="mt-3">
              <Button
                variant="primary"
                size="sm"
                loading={loading}
                disabled={!file}
                onClick={handleImport}
              >
                Import Staff
              </Button>
            </div>
            {result && (
              <>
                <ResultPanel result={result} />
                <TempCredentialsPanel credentials={result.temporaryCredentials} />
              </>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

// ─── Section: CampMinder Sync ─────────────────────────────────────────────────

function CampMinderSyncSection() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [intStatus, setIntStatus] = useState<IntegrationStatus | null>(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [allYearsMode, setAllYearsMode] = useState(false);
  const [fromYear, setFromYear] = useState(new Date().getFullYear() - 5);

  const [cmApiKey, setCmApiKey] = useState("");
  const [cmSubKey, setCmSubKey] = useState("");
  const [cmSiteId, setCmSiteId] = useState("");
  const [credSaving, setCredSaving] = useState(false);
  const [credConfirmOpen, setCredConfirmOpen] = useState(false);
  const [credEditing, setCredEditing] = useState(false);

  const [purgeConfirming, setPurgeConfirming] = useState(false);
  const [purgeConfirmText, setPurgeConfirmText] = useState("");
  const [purgeLoading, setPurgeLoading] = useState(false);
  const [purgeResult, setPurgeResult] = useState<PurgeResult | null>(null);

  useEffect(() => {
    integrationsApi.getStatus().then(setIntStatus).catch(() => {});
  }, []);

  const handleSaveCredentials = async () => {
    const payload: OrgCredentials = {};
    if (cmApiKey.trim()) payload.campMinderApiKey = cmApiKey.trim();
    if (cmSubKey.trim()) payload.campMinderSubscriptionKey = cmSubKey.trim();
    if (cmSiteId.trim()) payload.campMinderSiteId = cmSiteId.trim();
    setCredSaving(true);
    try {
      await integrationsApi.saveCredentials(payload);
      toast.success("CampMinder credentials saved.");
      setCredConfirmOpen(false);
      setCmApiKey("");
      setCmSubKey("");
      setCmSiteId("");
      setCredEditing(false);
      integrationsApi.getStatus().then(setIntStatus).catch(() => {});
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save credentials");
    } finally {
      setCredSaving(false);
    }
  };

  const handleOpenCredConfirm = () => {
    const hasValue = cmApiKey.trim() || cmSubKey.trim() || cmSiteId.trim();
    if (!hasValue) {
      toast.error("Enter at least one credential to save.");
      return;
    }
    setCredConfirmOpen(true);
  };

  const handlePurge = async () => {
    setPurgeLoading(true);
    setPurgeResult(null);
    try {
      const result = await integrationsApi.purgeNonCampMinder();
      setPurgeResult(result);
      setPurgeConfirming(false);
      setPurgeConfirmText("");
      toast.success(`Removed ${result.deleted} non-CampMinder record${result.deleted !== 1 ? "s" : ""}.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Purge failed");
    } finally {
      setPurgeLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncLoading(true);
    setSyncResult(null);
    try {
      const result = await integrationsApi.sync(
        allYearsMode ? { allYears: true, fromYear } : {}
      );
      setSyncResult(result);
      toast.success(`Sync complete — ${result.upserted} records updated`);
      const updated = await integrationsApi.getStatus();
      setIntStatus(updated);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Sync failed");
    } finally {
      setSyncLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title="CampMinder Sync"
        subtitle="Automated — pulls campers, cabins, divisions, and sessions directly from CampMinder. No CSV required."
      />

      {/* Status row */}
      <div className="flex flex-wrap gap-6 mb-4 text-sm">
        <div className="flex items-center gap-1.5">
          {intStatus?.configured ? (
            <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
          ) : (
            <span className="inline-block w-2 h-2 rounded-full bg-stone-300" />
          )}
          <span className="text-stone-600">
            {intStatus === null
              ? "Checking…"
              : intStatus.configured
              ? "Configured"
              : "Not configured"}
          </span>
        </div>
        <div className="text-stone-500">
          Last synced:{" "}
          <span className="font-medium text-stone-700">
            {intStatus?.lastSyncedAt
              ? new Date(intStatus.lastSyncedAt).toLocaleString()
              : "Never"}
          </span>
        </div>
      </div>

      {/* Primary sync button */}
      <Button
        variant="primary"
        loading={syncLoading}
        disabled={!intStatus?.configured || syncLoading}
        onClick={handleSync}
      >
        {allYearsMode ? "Sync All Years from CampMinder" : "Sync from CampMinder Now"}
      </Button>

      {/* Historical sync option */}
      <div className="mt-4 mb-6">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={allYearsMode}
            onChange={(e) => setAllYearsMode(e.target.checked)}
            className="h-4 w-4 rounded border-stone-300 text-green-600 focus:ring-green-500"
          />
          <span className="text-sm text-stone-700">Sync all years (historical data)</span>
        </label>
        {allYearsMode && (
          <>
            <div className="mt-2 ml-6 flex items-center gap-2">
              <label className="text-xs text-stone-600 whitespace-nowrap">From year:</label>
              <input
                type="number"
                min={1920}
                max={new Date().getFullYear()}
                value={fromYear}
                onChange={(e) => setFromYear(parseInt(e.target.value, 10))}
                className="w-24 rounded border border-stone-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <span className="text-xs text-stone-500">through {new Date().getFullYear()}</span>
            </div>
            <p className="mt-1 ml-6 text-xs text-stone-400">
              Historical sync may take several minutes depending on how many years are included.
            </p>
          </>
        )}
      </div>

      {/* Credential entry — admin only */}
      {isAdmin && (
        <div className="mb-6 space-y-3 border-t border-stone-100 pt-6">
          <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
            Update Credentials
          </p>
          {!credEditing ? (
            <>
              <p className="text-xs text-stone-500">
                Credentials are write-only and hidden by default. Click below to enter new values.
              </p>
              <Button
                variant="secondary"
                onClick={() => setCredEditing(true)}
              >
                Edit Credentials…
              </Button>
            </>
          ) : (
            <>
              <p className="text-xs text-stone-500">
                Leave a field blank to keep the existing value. Credentials are write-only — they are never returned by the API.
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1">
                    API Key
                  </label>
                  <input
                    type="password"
                    placeholder="Paste API Key…"
                    value={cmApiKey}
                    onChange={(e) => setCmApiKey(e.target.value)}
                    autoComplete="off"
                    className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1">
                    Subscription Key
                  </label>
                  <input
                    type="password"
                    placeholder="Paste Subscription Key…"
                    value={cmSubKey}
                    onChange={(e) => setCmSubKey(e.target.value)}
                    autoComplete="off"
                    className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1">
                    Site ID <span className="text-stone-400">(optional)</span>
                  </label>
                  <input
                    placeholder="e.g. highlands"
                    value={cmSiteId}
                    onChange={(e) => setCmSiteId(e.target.value)}
                    autoComplete="off"
                    className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  disabled={credSaving}
                  onClick={handleOpenCredConfirm}
                >
                  Save Credentials…
                </Button>
                <Button
                  variant="ghost"
                  disabled={credSaving}
                  onClick={() => {
                    setCredEditing(false);
                    setCmApiKey("");
                    setCmSubKey("");
                    setCmSiteId("");
                  }}
                >
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Sync result panel */}
      {syncResult && (
        <div className={`mt-4 rounded-lg p-4 text-sm ${syncResult.errors.length > 0 ? "bg-red-50 border border-red-200" : "bg-green-50 border border-green-200"}`}>
          <p className={`font-medium ${syncResult.errors.length > 0 ? "text-red-700" : "text-green-700"}`}>
            {syncResult.upserted} record{syncResult.upserted !== 1 ? "s" : ""} synced
            {syncResult.years && syncResult.years.length > 1
              ? ` for ${syncResult.years[0]}–${syncResult.years[syncResult.years.length - 1]}`
              : syncResult.seasonYear
              ? ` for ${syncResult.seasonYear}`
              : ""}
          </p>
          {(syncResult.removed ?? 0) > 0 && (
            <p className="mt-1 text-stone-600">
              {syncResult.removed} stale term enrollment{syncResult.removed !== 1 ? "s" : ""} removed
              (no longer assigned in CampMinder)
            </p>
          )}
          {syncResult.reconcile && (
            <p className="mt-1 text-stone-600">
              Reconcile: {syncResult.reconcile.checked} checked ·{" "}
              {syncResult.reconcile.unenrolled} unenrolled ·{" "}
              {syncResult.reconcile.orphaned} newly orphaned
              {syncResult.reconcile.alreadyOrphaned > 0 &&
                ` · ${syncResult.reconcile.alreadyOrphaned} already orphaned`}
            </p>
          )}
          {syncResult.errors.length > 0 && (
            <ul className="mt-2 space-y-1 text-red-600 list-disc list-inside">
              {syncResult.errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Purge non-CampMinder data — admin only */}
      {isAdmin && (
        <div className="mt-6 pt-6 border-t border-stone-100">
          <p className="text-sm font-medium text-stone-700 mb-1">Remove Non-CampMinder Data</p>
          <p className="text-xs text-stone-500 mb-3">
            Permanently deletes campers, terms, cabins, divisions, and all related records
            (enrollments, completions, honors) that were manually entered and are not linked
            to a CampMinder record. This cannot be undone.
          </p>

          {!purgeConfirming ? (
            <Button
              variant="danger"
              disabled={purgeLoading}
              onClick={() => setPurgeConfirming(true)}
            >
              Remove Non-CampMinder Data…
            </Button>
          ) : (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 space-y-3">
              <p className="text-sm font-semibold text-red-700">
                Are you sure? This will permanently delete all manually-entered data.
              </p>
              <div>
                <p className="text-xs text-red-700 mb-1">
                  Type <strong>DELETE</strong> to confirm:
                </p>
                <input
                  type="text"
                  value={purgeConfirmText}
                  onChange={(e) => setPurgeConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="rounded border border-red-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 w-32 bg-white"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  variant="danger"
                  loading={purgeLoading}
                  disabled={purgeLoading || purgeConfirmText !== "DELETE"}
                  onClick={handlePurge}
                >
                  Yes, delete it
                </Button>
                <Button
                  variant="ghost"
                  disabled={purgeLoading}
                  onClick={() => { setPurgeConfirming(false); setPurgeConfirmText(""); }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {purgeResult && (
            <div className="mt-3 rounded-lg bg-stone-50 border border-stone-200 p-3 text-xs text-stone-600">
              <p className="font-medium text-stone-700 mb-1">Deleted {purgeResult.deleted} records:</p>
              <ul className="space-y-0.5">
                {Object.entries(purgeResult.breakdown)
                  .filter(([, n]) => n > 0)
                  .map(([key, n]) => (
                    <li key={key}>{n} {key}</li>
                  ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        open={credConfirmOpen}
        onClose={() => setCredConfirmOpen(false)}
        onConfirm={handleSaveCredentials}
        title="Update CampMinder Credentials"
        message="This will permanently overwrite any currently stored credentials for the fields you've filled in. All CampMinder sync operations will use the new values immediately."
        confirmLabel="Save Credentials"
        variant="primary"
        loading={credSaving}
      />
    </Card>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ManageImports() {
  const [activities, setActivities] = useState<Activity[]>([]);

  useEffect(() => {
    activitiesApi.list().then(setActivities).catch(() => {});
  }, []);

  return (
    <div>
      <PageHeader
        title="Import & Export"
        subtitle="Use CampMinder Sync for automated data management, or manually import/export data via CSV."
      />

      <div className="p-6 lg:p-8">
        <div className="max-w-3xl space-y-4">

          {/* ── CampMinder Sync (Automated) ─────────────────────────────── */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-semibold text-blue-700 bg-blue-100 px-2 py-0.5 rounded uppercase tracking-wide">
                Automated
              </span>
              <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
                CampMinder Sync
              </p>
            </div>
            <CampMinderSyncSection />
          </div>

          {/* ── Manual Import / Export ──────────────────────────────────── */}
          <div className="pt-6 border-t border-stone-200">
            <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-1">
              Manual Import / Export
            </p>
            <p className="text-xs text-stone-400 mb-4">
              CSV-based bulk operations. These do not affect CampMinder and operate independently of sync.
            </p>
            <div className="flex items-start gap-2 text-xs text-stone-500 bg-stone-50 border border-stone-200 rounded-lg p-3 mb-4">
              <InformationCircleIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-stone-400" />
              <span>
                Most imports are <strong>add-only</strong>. ACs support replace/delete modes (single activity) and
                update-or-create (bulk across all activities). Duplicate records are skipped based on the identifier
                noted in each section. Download the blank template or export existing data as a starting point.
              </span>
            </div>
          </div>

          <AcsSection activities={activities} />
          <AcsBulkSection />

          <GenericSection
            title="Activities"
            subtitle="Add new activities (duplicate check: name)"
            icon={<span className="text-base">⚡</span>}
            columns="name, description, location"
            type="activities"
            importFn={(f) => importsApi.importActivities(f)}
            extraNotes={undefined}
          />

          <GenericSection
            title="Campers"
            subtitle="Add new campers (duplicate check: first name + last name + date of birth)"
            icon={<span className="text-base">🏕️</span>}
            columns="first_name, last_name, date_of_birth, team"
            type="campers"
            importFn={(f) => importsApi.importCampers(f)}
            extraNotes={
              <>
                <p className="text-xs text-stone-400 mt-1">
                  Date format: YYYY-MM-DD or MM/DD/YYYY. Team: army or navy (optional).
                </p>
                <p className="flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-2">
                  <ExclamationTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  CampMinder manages camper records. Manually imported campers are not linked to CampMinder and must be managed separately — they will not be removed or updated by sync.
                </p>
              </>
            }
          />

          <CounselorsSection />

          <GenericSection
            title="Cabins"
            subtitle="Add new cabins (duplicate check: name)"
            icon={<span className="text-base">🏠</span>}
            columns="name, cabin_group_name"
            type="cabins"
            importFn={(f) => importsApi.importCabins(f)}
            extraNotes={
              <>
                <p className="text-xs text-stone-400 mt-1">
                  <code className="font-mono">cabin_group_name</code> is optional.
                  If provided, the cabin group must already exist in{" "}
                  <a href="/admin/cabins" className="underline hover:text-stone-600">Manage Cabins</a>.
                </p>
                <p className="flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-2">
                  <ExclamationTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  CampMinder manages cabin records. Manually imported cabins are not linked to CampMinder and must be managed separately — they will not be removed or updated by sync.
                </p>
              </>
            }
          />
        </div>
      </div>
    </div>
  );
}
